// @flow
import negate from 'lodash/fp/negate';
import flatten from 'lodash/fp/flatten';
import isEmpty from 'lodash/fp/isEmpty';
import takeWhile from 'lodash/fp/takeWhile';
import difference from 'lodash/fp/difference';
import lodashChunk from 'lodash/fp/chunk';
import differenceWith from 'lodash/fp/differenceWith';
import mergeObjectsWith from 'lodash/fp/mergeWith';

import type { ROA } from './type';

/* ⚠️ there's probably a better way to get lodash into scramble4, but was a bigger undertaking */
export const chunk = lodashChunk;

const isValidIndex = (idx: number, arr: $ReadOnlyArray<any>): boolean =>
  idx < arr.length && idx >= 0;

export const nextIndex = (
  idx: number | null,
  arr: $ReadOnlyArray<any>,
): number | null => {
  if (arr.length === 0) {
    return null;
  }

  if (idx === null || !isValidIndex(idx, arr)) {
    return 0;
  }

  return (idx + 1) % arr.length;
};

export const previousIndex = (
  idx: number | null,
  arr: any[],
): number | null => {
  if (arr.length === 0) {
    return null;
  }
  const lastIdx = arr.length - 1;

  const indexIsValid = typeof idx === 'number' && isValidIndex(idx, arr);

  return indexIsValid && (idx || 0) > 0 ? (idx || 0) - 1 : lastIdx;
};

// doesn't guarantee any specific sequence in output, always new reference
export const change = (
  toAdd: any,
  toRemove: any,
  list: $ReadOnlyArray<any>,
): $ReadOnlyArray<any> => [
  ...list.filter((e) => !toAdd.includes(e) && !toRemove.includes(e)),
  ...toAdd,
];

export const modifyOne = <T>(list: T[], modifier: (T) => T): T[] =>
  list.reduce(
    ({ acc, modified }, item) => {
      if (modified) return { acc: [...acc, item], modified };

      const possiblyModifiedItem = modifier(item);

      return {
        acc: [...acc, possiblyModifiedItem],
        modified: possiblyModifiedItem !== item,
      };
    },
    { acc: [], modified: false },
  ).acc;

// TODO we actually want this function to look like below,
// but was hitting flow errors
/*
export const byId = <T: { id: string | number }>(
  haystack: T[],
  needle: string | number,
): T | void => haystack.find(({ id }) => id === needle);
*/

export const byId = <T: $ReadOnly<{ id: string }>>(
  haystack: T[],
  needle: string,
): T | void => haystack.find(({ id }) => id === needle);

// eslint-disable-next-line arrow-parens
export const strictlyMerged = <T>(...lists: Array<Array<T> | void>): Array<T> =>
  flatten(lists.filter(Boolean));

const strictlyMergedTwo = <T>(a: T[] | void, b: T[] | void): Array<T> =>
  strictlyMerged(a, b);

export const mergedObjectsOfArrays = <T>(
  list: Array<{ [string]: Array<T> }>,
): { [string]: Array<T> } =>
  list.reduce((acc, obj) => mergeObjectsWith(strictlyMergedTwo, obj, acc), {
    ...null,
  });

export type Finder<T> = (T, $ReadOnlyArray<T>) => T | void;
export type ItemMerger<T> = (T, T) => T;
export type Merger<T> = (
  $ReadOnlyArray<T>,
  $ReadOnlyArray<T>,
) => $ReadOnlyArray<T>;

export const mergeWith =
  <T>(finder: Finder<T>, merger: ItemMerger<T>): Merger<T> =>
  (listA, listB) => {
    const inBoth = listB.filter((itemB) => finder(itemB, listA));

    const bMergedIntoA = listA.map((itemA) => {
      const toMergeB = finder(itemA, inBoth);
      return toMergeB ? merger(itemA, toMergeB) : itemA;
    });

    return [
      ...bMergedIntoA,
      ...listB.filter((itemB) => !inBoth.includes(itemB)),
    ];
  };

export const asArray = <T>(subject: any): Array<T> =>
  Array.from(flatten([subject]));

// Use this when you want to do [some array].filter(identity)
// Flow currently can't refine array types using filter 😥
// https://github.com/facebook/flow/issues/1414
export const typedIdentityFilter = <T>(
  items: Array<T | void | null>,
): Array<T> => items.filter(Boolean);

export const aIsSubsetOfB = <T>(a: ROA<T>, b: ROA<T>): boolean =>
  isEmpty(difference(a, b));

/**
 * Takes two pre-sorted arrays and interleaves the values of the second into the
 * most logical places within the first based on where for whom `areEqual` is
 * true appear relatively between each. This runs in something like `O(4(n^2))`
 * time complexity.
 *
 * TODO: there might be a nice recursion-based solution to this problem
 */
export const interleaved = <T>(
  areEqual: (T, T) => boolean,
  a: $ReadOnlyArray<T>,
  b: $ReadOnlyArray<T>,
): $ReadOnlyArray<T> =>
  b.reduce((acc: $ReadOnlyArray<T>, element: T, idx: number) => {
    const atEnd = idx === b.length - 1;

    const remainingA = differenceWith(areEqual, a, acc);

    // i was gonna use the lodash `curry` helper here but it's a typing
    // mess and i worry about its performance
    const curriedAreEqual = (a1: T) => (b1: T) => areEqual(a1, b1);

    if (acc.find(curriedAreEqual(element))) {
      return [...acc, ...(atEnd ? remainingA : [])];
    }

    const beforeElement = takeWhile(
      negate(curriedAreEqual(element)),
      remainingA,
    );

    if (beforeElement.length !== remainingA.length) {
      // remainingA.includes(element)
      return [
        ...acc,
        ...beforeElement,
        element,
        ...(atEnd
          ? differenceWith(areEqual, remainingA, [...beforeElement, element])
          : []),
      ];
    }

    return [...acc, element, ...(atEnd ? remainingA : [])];
  }, ([]: $ReadOnlyArray<T>));

export const interspersed =
  <Joiner, T>(
    joinerBuilder: (number) => Joiner,
  ): (($ReadOnlyArray<T>) => $ReadOnlyArray<T | Joiner>) =>
  (list: $ReadOnlyArray<T>): $ReadOnlyArray<T | Joiner> =>
    list.reduce(
      (
        acc: $ReadOnlyArray<T | Joiner>,
        item: T,
        idx: number,
      ): $ReadOnlyArray<Joiner | T> => [
        ...acc,
        item,
        ...(idx === list.length - 1 ? [] : [joinerBuilder(idx)]),
      ],
      ([]: $ReadOnlyArray<T | Joiner>),
    );

export const groupByReference = <K, V>(
  keyBuilder: (V) => K,
  list: $ReadOnlyArray<V>,
): Map<K, $ReadOnlyArray<V>> => {
  const map = new Map<K, Array<V>>();

  list.forEach((item: V) => {
    const key: K = keyBuilder(item);

    const current = map.get(key);

    if (current) {
      // we're doing this with mutation because this is a low-level utility function
      // and this is the most performant way to accomplish this at scale
      // eslint-disable-next-line fp/no-mutating-methods
      current.push(item);
      return;
    }

    map.set(key, [item]);
  });

  // these should no longer mutate
  return ((map: any): Map<K, $ReadOnlyArray<V>>);
};

export const elementsAreEqual = <T>(elements: ROA<T>): boolean =>
  elements.every((e) => e === elements[0]);
